import React, { useState, useEffect } from 'react';
import { ParameterConfig } from '../../types/app';
import { LoanType } from '../../types/api';

interface SliderInputProps {
  config: ParameterConfig;
  value: any;
  onChange: (value: any) => void;
  disabled?: boolean;
}

export const SliderInput: React.FC<SliderInputProps> = ({
  config,
  value,
  onChange,
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  // Update input value when external value changes (but not when focused)
  useEffect(() => {
    if (!isFocused) {
      const formattedValue = config.formatValue ? config.formatValue(value) : value.toString();
      setInputValue(formattedValue);
    }
  }, [value, config, isFocused]);

  // Handle slider changes
  // const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const newValue = parseFloat(e.target.value);
  //   onChange(newValue);
  // };

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  // Handle input blur (when user finishes editing)
  const handleInputBlur = () => {
    setIsFocused(false);
    try {
      let newValue: any;

      // Special handling for percentage-like inputs to accept both "8" and "0.08"
      if (config.unit === '%') {
        const numeric = parseFloat(inputValue.replace(/[^0-9.-]/g, ''));
        if (!isNaN(numeric)) {
          // If user typed a fractional rate (<= 1), use as-is; if they typed a whole percent (>= 1), convert
          newValue = numeric <= 1 ? numeric : numeric / 100;
        } else {
          newValue = NaN;
        }
      } else if (config.parseValue) {
        newValue = config.parseValue(inputValue);
      } else {
        newValue = parseFloat(inputValue.replace(/[^0-9.-]/g, ''));
      }

      // Clamp to min/max bounds
      const isNumeric = typeof newValue === 'number' && !isNaN(newValue);

      if (!isNumeric) {
        // Reset to current value if invalid
        const formattedValue = config.formatValue ? config.formatValue(value) : value.toString();
        setInputValue(formattedValue);
        return;
      }

      // Accept out-of-range numeric values without clamping
      onChange(newValue);
    } catch (error) {
      // Reset to current value if parsing fails
      const formattedValue = config.formatValue ? config.formatValue(value) : value.toString();
      setInputValue(formattedValue);
    }
  };

  // Handle input focus
  const handleInputFocus = () => {
    setIsFocused(true);
    // Keep formatting visible for clarity; for percentages, show formatted with "%" to guide input
    const formatted = config.formatValue ? config.formatValue(value) : value.toString();
    setInputValue(formatted);
  };

  // Handle Enter key
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleInputBlur();
      e.currentTarget.blur();
    }
  };

  // Specialized rendering for Loan Type as a toggle
  if (config.key === 'loan_details.loan_type') {
    const current =
      value === LoanType.FHA || value === 'fha' || value === undefined
        ? LoanType.FHA
        : LoanType.CONVENTIONAL;
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-end">
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              type="button"
              disabled={disabled}
              onClick={() => onChange(LoanType.FHA)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                current === LoanType.FHA ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              FHA
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={() => onChange(LoanType.CONVENTIONAL)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                current === LoanType.CONVENTIONAL ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Conventional
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Input field */}
      <div className="flex items-center justify-end">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          disabled={false}
          className="w-20 px-2 py-1 text-sm text-right border border-gray-300 rounded focus:border-blue-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-500"
        />
      </div>

      {/* Slider */}
      <div className="relative">
        {/* Custom track */}
        <div className="w-full h-1 bg-gray-300 rounded-full relative">
          {/* Progress fill */}
          <div 
            className={`h-full rounded-full ${
              value > config.max ? 'bg-orange-400' : 'bg-blue-500'
            }`}
            style={{
              width: `${Math.max(0, Math.min(100, ((value - config.min) / (config.max - config.min)) * 100))}%`
            }}
          />
          {/* Thumb */}
          <div 
            className={`absolute top-1/2 transform -translate-y-1/2 w-4 h-4 rounded-full border-2 border-white shadow-md cursor-pointer transition-colors ${
              value > config.max 
                ? 'bg-orange-500 hover:bg-orange-600' 
                : 'bg-blue-500 hover:bg-blue-600'
            }`}
            style={{
              left: `calc(${Math.max(0, Math.min(100, ((value - config.min) / (config.max - config.min)) * 100))}% - 8px)`
            }}
            onMouseDown={(e) => {
              const slider = e.currentTarget.parentElement;
              if (!slider) return;
              
              const rect = slider.getBoundingClientRect();
              const handleMove = (e: MouseEvent) => {
                const x = e.clientX - rect.left;
                const percentage = Math.max(0, Math.min(1, x / rect.width));
                const newValue = config.min + percentage * (config.max - config.min);
                const steppedValue = Math.round(newValue / config.step) * config.step;
                const clampedValue = Math.max(config.min, Math.min(config.max, steppedValue));
                onChange(clampedValue);
              };
              
              const handleUp = () => {
                document.removeEventListener('mousemove', handleMove);
                document.removeEventListener('mouseup', handleUp);
              };
              
              document.addEventListener('mousemove', handleMove);
              document.addEventListener('mouseup', handleUp);
            }}
          />
        </div>

        {/* Min/Max labels */}
        <div className="flex justify-between mt-1 text-xs text-gray-500">
          <span>
            {config.formatValue ? config.formatValue(config.min) : config.min}
          </span>
          <span>
            {config.formatValue ? config.formatValue(config.max) : config.max}
          </span>
        </div>
      </div>
    </div>
  );
};
